import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import firebase from "firebase/compat/app";

const CustomPage = ({ restaurantID, pages }) => {
  const [docId, setDocId] = useState("");
  const { pageName } = useParams();
  
  // This ref will hold the container where we inject the raw HTML
  const containerRef = useRef(null);

  // 1. Data Fetching
  useEffect(() => {
    const targetPage = pages?.find(p => p["PageName"] === pageName);
    const rawDocId = targetPage?.["Link"];
    setDocId(rawDocId);
  }, [pages, pageName]);

  // 2. Construct the HTML String
  // (Note: I removed 'DOMContentLoaded' because the event has already passed.
  // The script runs immediately after insertion, so the elements exist.)
  const getRawHtmlString = (currentDocId, restaurantId) => {
    const safeDocId = currentDocId ? String(currentDocId).replace(/'/g, "\\'") : '';
    const safeRestaurantId = restaurantId ? String(restaurantId).replace(/'/g, "\\'") : '';

    const css = `
      .back-button-container {
        position: fixed; top: 25px; left: 25px; background-color: transparent; z-index: 1000; cursor: pointer;
      }
      .back-button-icon {
        position: fixed; width: 40px; height: 40px; display: flex; flex-wrap: wrap; justify-content: center;
        align-items: center; background-color: #FFFFFF99;
        box-shadow: inset 0px 0px 25px #FFFFFF25, 0px 0px 15px #001E3A25;
        z-index: 1000; cursor: pointer; border-radius: 50px; padding-left: 8px;
      }
      .content-embed {
        width: 100vw;
      }
    `;

    const script = `
      (function() {
        console.log("Injecting Script for Restaurant:", '${safeRestaurantId}');
        window.RESTAURANT_ID = '${safeRestaurantId}';
        window.DOC_ID = '${safeDocId}';

        window.handleBackClick = function(e) {
          const timestamp = new Date();
          if (window.gtag) {
            window.gtag("event", \`\${window.RESTAURANT_ID}_\${e}_Click\`, {
              timestamp: timestamp.toLocaleString(),
              click: e,
              restaurant: window.RESTAURANT_ID,
              docId: window.DOC_ID,
            });
          }
          window.location.href = "/";
        };

        // RUN IMMEDIATELY (Do not wait for DOMContentLoaded)
        // Since this script is injected after HTML, the element exists.
        const embedElement = document.getElementById('custom-content-embed');
        if (embedElement && window.DOC_ID) {
          const docLower = window.DOC_ID.toLowerCase();
          let height;
          if (docLower.includes('.pdf')) {
            height = '100vh';
          } else if (docLower.includes(".png") || docLower.includes(".jpg") || docLower.includes(".jpeg") || docLower.includes(".webp") || docLower.includes(".heic")) {
            height = '100%';
          } else {
            height = '100vh';
          }
          embedElement.style.height = height;
          embedElement.height = height;
        }
      })();
    `;

    const initialEmbedHeight = safeDocId.toLowerCase().includes('.pdf') ? "100vh" : "100%";
    
    return `
      <style>${css}</style>
      <div class="back-button-container" onclick="window.handleBackClick('Back_Button')">
        <div class="back-button-icon">
          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#001E3A">
            <path d="M400-80 0-480l400-400 71 71-329 329 329 329-71 71Z" />
          </svg>
        </div>
      </div>
      ${safeDocId ? `<embed id="custom-content-embed" src="${safeDocId}" width="100vw" height="${initialEmbedHeight}" class="content-embed" />` : '<div>Loading content...</div>'}
      <script>${script}</script>
    `;
  };

  // 3. The Magic: Inject HTML and Force-Run Scripts
  useEffect(() => {
    // Guard: Wait for data
    if (!restaurantID || !docId || !containerRef.current) return;

    const rawHtml = getRawHtmlString(docId, restaurantID);

    // A. Insert the HTML (CSS and HTML structure appear, but Scripts are dead)
    containerRef.current.innerHTML = rawHtml;

    // B. Find all scripts in the injected HTML
    const scripts = containerRef.current.querySelectorAll("script");

    // C. Re-create and execute them
    scripts.forEach((oldScript) => {
      const newScript = document.createElement("script");
      
      // Copy attributes (like src, type, etc.)
      Array.from(oldScript.attributes).forEach((attr) => {
        newScript.setAttribute(attr.name, attr.value);
      });

      // Copy the content (the actual JS code)
      newScript.appendChild(document.createTextNode(oldScript.innerHTML));

      // Replace old script with new script in the DOM to trigger execution
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });

    // Cleanup: Remove global function on unmount
    return () => {
      delete window.handleBackClick;
    };

  }, [restaurantID, docId]); // Rerun if data changes

  if (!restaurantID) return <div>Loading...</div>;

  // Render a plain div with a ref. content is injected by useEffect.
  return <div ref={containerRef} />;
};

export default CustomPage;